import moment from "moment";
import {
  dailyTargetIds,
  DateFormat,
  getMacronutrientColor,
  kcalMultiplier,
} from "../modules/constants";

export const decimalPlaces = 2;

export const emptyTotals = {
  calories: "0.00 kcal",
  totalFats: "0.00 g",
  longChainFats: "0.00 g",
  mediumChainFats: "0.00 g",
};

export const fastedMealIndicatorReport = "Fasted";

export const fastingDayEmptyMeal =
  '{"aboutMeal":[{}],"items":[{}],"type":"food_drink"}';

export const roundMealValue = (n) =>
  Math.round(parseFloat(n) * Math.pow(10, decimalPlaces)) /
  Math.pow(10, decimalPlaces);

export const getRoundedValue = (source) => {
  if (source === "") {
    source = 0;
  }
  return Number(roundMealValue(source ?? 0)).toFixed(decimalPlaces);
};

export const calculateMealPercentages = (meal) => {
  const totalValues = meal.calories.value;

  meal.protein.percentage = 0;
  meal.carbohydrates.percentage = 0;
  meal.fats.percentage = 0;

  if (totalValues > 0) {
    meal.protein.percentage = (meal.protein.kcal / totalValues) * 100;
    meal.carbohydrates.percentage =
      (meal.carbohydrates.kcal / totalValues) * 100;

    meal.fats.percentage = (meal.fats.kcal / totalValues) * 100;
  }

  return meal;
};

//d = denominator
export const calculateAvg = (data, d) => {
  let average = {
    ...data,
  };

  const denominator = d || data.completed;

  if (denominator > 0) {
    average = {
      ...data,
      calories: {
        ...data.calories,
        value: data.calories.value / denominator,
      },
      protein: {
        ...data.protein,
        value: data.protein.value / denominator,
        percentage: data.protein.percentage / denominator,
        kcal: data.protein.kcal && data.protein.kcal / denominator,
      },
      carbohydrates: {
        ...data.carbohydrates,
        value: data.carbohydrates.value / denominator,
        percentage: data.carbohydrates.percentage / denominator,
        kcal: data.carbohydrates.kcal && data.carbohydrates.kcal / denominator,
      },
      fats: {
        ...data.fats,
        value: data.fats.value / denominator,
        percentage: data.fats.percentage / denominator,
        kcal: data.fats.kcal && data.fats.kcal / denominator,
      },
      sugars: {
        ...data.sugars,
        value: data.sugars.value / denominator,
      },
    };
  }

  return average;
};

export const aggregateAll = (accumulator, item) => {
  return {
    ...accumulator,
    calories: {
      value: (accumulator?.calories?.value ?? 0) + Number(item.calories),
    },
    protein: {
      percentage:
        (accumulator?.protein?.percentage ?? 0) +
        Number(item.protein.split(" %")[0]),
    },
    fats: {
      percentage:
        (accumulator?.fats?.percentage ?? 0) + Number(item.fats.split(" %")[0]),
    },
    carbohydrates: {
      percentage:
        (accumulator?.carbohydrates?.percentage ?? 0) +
        Number(item.carbohydrates.split(" %")[0]),
    },
    sugars: {
      value:
        (accumulator?.sugars?.value ?? 0) + Number(item.sugars.split(" g")[0]),
    },
    completed: (accumulator.completed ?? 0) + 1,
  };
};

export const isBetweenDates = (week, date) => {
  return moment(date).isBetween(
    moment(week[0]).startOf("day"),
    moment(week[1]).endOf("day"),
    null,
    "[]"
  ); //inclusive
};

//Generate weeks given a date range
export const generateWeeks = (startDay, endDay) => {
  const weeks = [];
  const startDate = moment(startDay);
  const endDate = moment(endDay);

  while (startDate.isBefore(endDate)) {
    let startWeek = startDate.clone().startOf("day");
    let endDateWeek = startWeek.clone().endOf("day").add(6, "days");

    startDate.add(7, "days");
    weeks.push([
      startWeek.format(DateFormat.INVERTED_DATE),
      endDateWeek.format(DateFormat.INVERTED_DATE),
    ]);
  }
  return weeks;
};

export const calculateKcal = (item) => {
  return {
    ...item,
    carbohydrates: {
      ...item.carbohydrates,
      kcal: item.carbohydrates.value * kcalMultiplier.carbs,
    },
    fats: {
      ...item.fats,
      kcal: item.fats.value * kcalMultiplier.fats,
    },
    protein: {
      ...item.protein,
      kcal: item.protein.value * kcalMultiplier.protein,
    },
  };
};

export const calculateKcalAndPercentage = (meal) => {
  const kcal = calculateKcal(meal);
  const percentages = calculateMealPercentages(kcal);
  return {
    ...meal,
    ...kcal,
    ...percentages,
  };
};

export const getFinalKcalAndPercentage = (finalData) => {
  return finalData.map((item) => {
    //Per day
    const kcalperDay = calculateKcal(item);
    const percentagesPerDay = calculateMealPercentages(kcalperDay);

    //for each meal
    const mealsKcal = item.meals.map((meal) => {
      return calculateKcalAndPercentage(meal);
    });
    return { ...item, ...kcalperDay, ...percentagesPerDay, meals: mealsKcal };
  });
};

export const calcKcalByAttrId = (value, id) => {
  switch (id) {
    case dailyTargetIds.CARBS_ATTR_ID:
      return value * kcalMultiplier.carbs;
    case dailyTargetIds.FATS_ATTR_ID:
      return value * kcalMultiplier.fats;
    case dailyTargetIds.PROTEIN_ATTR_ID:
      return value * kcalMultiplier.protein;
    default:
      return value;
  }
};

export const addOtherSlice = (data, theme, studyId, t) => {
  const otherfrq = 100 - data.reduce((acc, item) => acc + item?.frequency, 0);

  return {
    letter: t("appText.other"),
    frequency: otherfrq,
    color: getMacronutrientColor("", theme, studyId),
    percentage: "",
    info: "",
    subInfo: "",
    isVisible: false,
  };
};

export const isFastingDay = (entryItems) => {
  return (
    entryItems?.json === fastingDayEmptyMeal ||
    (entryItems?.meals &&
      entryItems?.meals[0] &&
      entryItems?.meals?.length === 1 &&
      (entryItems?.meals[0]?.fastingDay ||
        entryItems?.meals[0]?.json === fastingDayEmptyMeal ||
        entryItems?.meals[0]?.mealJson === fastingDayEmptyMeal))
  );
};

export const areTotalsEmpty = (totals) => {
  if (
    totals.calories === emptyTotals.calories &&
    totals.longChainFats === emptyTotals.longChainFats &&
    totals.mediumChainFats === emptyTotals.mediumChainFats &&
    totals.totalFats === emptyTotals.totalFats
  ) {
    return true;
  }

  return false;
};
